import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  CheckCircle,
  Copy,
  Lightbulb,
  ShieldWarning,
  Target,
  TrendDown,
  TrendUp,
} from "@phosphor-icons/react";
import type { CriativoAnalysis } from "../types";

export function AnalysisResult({
  analysis,
  transcript,
  veiculacao,
}: {
  analysis: CriativoAnalysis;
  transcript: string;
  veiculacao: string;
}) {
  if (analysis.parse_error) {
    return (
      <div className="bg-card border border-border/50 rounded-xl p-6">
        <p className="text-sm font-medium text-foreground mb-2">
          Resposta da IA (formato bruto)
        </p>
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-words">
          {analysis.raw_text}
        </pre>
      </div>
    );
  }

  const score = analysis.score_geral;
  const scoreColor =
    score == null
      ? "text-muted-foreground"
      : score >= 8
        ? "text-green-400"
        : score >= 6
          ? "text-yellow-400"
          : "text-destructive";

  return (
    <div className="bg-card border border-border/50 rounded-xl p-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CheckCircle weight="duotone" className="w-5 h-5 text-green-400" />
            <span className="text-sm font-medium text-foreground">Análise concluída</span>
          </div>
          {analysis.objetivo_detectado && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Target weight="duotone" className="w-4 h-4 text-primary" />
              <span className="text-sm text-foreground">
                <span className="font-medium">Objetivo detectado:</span>{" "}
                {analysis.objetivo_detectado}
              </span>
              {analysis.objetivo_confianca && (
                <Badge variant="outline" className="text-xs">
                  {analysis.objetivo_confianca}
                </Badge>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Veiculação: {veiculacao}
            {analysis.tipo_conteudo ? ` · ${analysis.tipo_conteudo}` : ""}
          </p>
        </div>
        {score != null && (
          <div className="text-right">
            <p className={cn("text-3xl font-bold tabular-nums", scoreColor)}>
              {score.toFixed(1)}
            </p>
            <p className="text-xs text-muted-foreground">Score geral</p>
          </div>
        )}
      </div>

      {analysis.resumo_executivo && (
        <p className="text-sm text-foreground/80 italic border-l-2 border-primary/40 pl-3">
          {analysis.resumo_executivo}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {analysis.pontos_fortes && analysis.pontos_fortes.length > 0 && (
          <Section
            title="Pontos fortes"
            icon={<TrendUp weight="duotone" className="w-4 h-4 text-green-400" />}
            items={analysis.pontos_fortes}
            tone="positive"
          />
        )}
        {analysis.pontos_fracos && analysis.pontos_fracos.length > 0 && (
          <Section
            title="Pontos fracos"
            icon={<TrendDown weight="duotone" className="w-4 h-4 text-yellow-400" />}
            items={analysis.pontos_fracos}
            tone="warning"
          />
        )}
        {analysis.sugestoes && analysis.sugestoes.length > 0 && (
          <Section
            title="Sugestões"
            icon={<Lightbulb weight="duotone" className="w-4 h-4 text-primary" />}
            items={analysis.sugestoes}
            tone="info"
          />
        )}
        {analysis.riscos_compliance && analysis.riscos_compliance.length > 0 && (
          <Section
            title="Riscos / compliance"
            icon={<ShieldWarning weight="duotone" className="w-4 h-4 text-destructive" />}
            items={analysis.riscos_compliance}
            tone="danger"
          />
        )}
      </div>

      {(analysis.hook_analise || analysis.cta_analise) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {analysis.hook_analise && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                Hook
              </p>
              <p className="text-sm text-foreground/90">{analysis.hook_analise}</p>
            </div>
          )}
          {analysis.cta_analise && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                CTA
              </p>
              <p className="text-sm text-foreground/90">{analysis.cta_analise}</p>
            </div>
          )}
        </div>
      )}

      {analysis.legenda_sugerida && (
        <div className="bg-muted/30 border border-border/40 rounded-lg p-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Legenda sugerida
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary"
              onClick={() => {
                void navigator.clipboard
                  .writeText(analysis.legenda_sugerida ?? "")
                  .then(() => toast.success("Legenda copiada!"));
              }}
            >
              <Copy weight="regular" className="w-3.5 h-3.5" />
              Copiar
            </Button>
          </div>
          <p className="text-sm text-foreground/90 whitespace-pre-wrap">
            {analysis.legenda_sugerida}
          </p>
        </div>
      )}

      {transcript && (
        <details className="bg-muted/20 border border-border/40 rounded-lg" open>
          <summary className="px-3 py-2 text-xs font-semibold text-muted-foreground cursor-pointer">
            Transcrição do áudio
          </summary>
          <p className="text-sm text-foreground/85 px-3 pb-3 whitespace-pre-wrap">
            {transcript}
          </p>
        </details>
      )}
    </div>
  );
}

function Section({
  title,
  icon,
  items,
  tone,
}: {
  title: string;
  icon: React.ReactNode;
  items: string[];
  tone: "positive" | "warning" | "info" | "danger";
}) {
  const dotColor = {
    positive: "bg-green-400",
    warning: "bg-yellow-400",
    info: "bg-primary",
    danger: "bg-destructive",
  }[tone];
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </p>
      </div>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-sm text-foreground/90">
            <span className={cn("w-1 h-1 rounded-full mt-2 shrink-0", dotColor)} />
            <span className="flex-1">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
